import { MapPin, Phone, Mail, Instagram, ArrowRight, Sparkles } from "lucide-react";

const ADDRESS = "18 Rue Gambetta, 01000 Bourg-en-Bresse";
const PHONE = "04 82 84 95 60";
const PHONE_RAW = "0482849560";
const EMAIL = "contact@arlequinglacier.fr";
const INSTAGRAM_HANDLE = "@arlequin_glacier";
const INSTAGRAM_URL = "https://instagram.com/arlequin_glacier";
const MAP_QUERY = encodeURIComponent(ADDRESS);
const MAP_EMBED = `https://maps.google.com/maps?q=${MAP_QUERY}&t=&z=16&ie=UTF8&iwloc=&output=embed`;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`;

const SCHEDULE = [
    { day: "Lundi", short: "Lun", hours: "Fermé", closed: true },
    { day: "Mardi", short: "Mar", hours: "15:00 – 19:00" },
    { day: "Mercredi", short: "Mer", hours: "13:00 – 19:00" },
    { day: "Jeudi", short: "Jeu", hours: "15:00 – 19:00" },
    { day: "Vendredi", short: "Ven", hours: "15:00 – 19:00" },
    { day: "Samedi", short: "Sam", hours: "13:00 – 19:00" },
    { day: "Dimanche", short: "Dim", hours: "Fermé", closed: true },
];

function ContactCard({ icon: Icon, label, value, href, testid, external }) {
    const Wrapper = href ? "a" : "div";
    const wrapperProps = href
        ? { href, ...(external ? { target: "_blank", rel: "noreferrer" } : {}) }
        : {};
    return (
        <Wrapper
            {...wrapperProps}
            data-testid={testid}
            className={`group flex items-center gap-4 rounded-2xl bg-white border border-arl-blue/10 p-5 transition-all ${
                href ? "hover:-translate-y-0.5 hover:border-arl-blue/30 hover:shadow-lg" : ""
            }`}
        >
            <span className="w-11 h-11 rounded-xl bg-arl-blue/10 text-arl-blue flex items-center justify-center shrink-0 group-hover:bg-arl-blue group-hover:text-white transition-colors">
                <Icon size={20} />
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.22em] text-arl-blue/60 font-semibold">
                    {label}
                </p>
                <p className="mt-0.5 font-display text-base sm:text-lg text-arl-ink leading-snug break-words">
                    {value}
                </p>
            </div>
        </Wrapper>
    );
}

function ScheduleStrip() {
    return (
        <section className="relative pb-20 sm:pb-28" data-testid="contact-schedule">
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
                <div className="relative rounded-[2rem] bg-white border border-arl-blue/10 shadow-[0_24px_60px_-30px_rgba(36,85,99,0.25)] p-6 sm:p-8 overflow-hidden">
                    <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.22em] text-arl-blue/60 font-semibold">
                                La semaine
                            </p>
                            <h2 className="mt-2 font-display text-2xl sm:text-3xl text-arl-ink leading-tight">
                                Horaires d'ouverture
                            </h2>
                        </div>
                        <p className="text-xs text-arl-ink/55 max-w-xs">
                            Horaires susceptibles d'évoluer selon les saisons. Suivez l'Instagram pour
                            les annonces.
                        </p>
                    </div>

                    <ul
                        className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3"
                        data-testid="schedule-days"
                    >
                        {SCHEDULE.map((d) => (
                            <li
                                key={d.day}
                                data-testid={`schedule-${d.short.toLowerCase()}`}
                                className={`rounded-2xl px-3 py-4 text-center transition ${
                                    d.closed
                                        ? "bg-arl-paper border border-arl-blue/5"
                                        : "bg-arl-blue text-arl-cream shadow-[0_10px_24px_-16px_rgba(36,85,99,0.6)]"
                                }`}
                            >
                                <p
                                    className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 ${
                                        d.closed ? "text-arl-ink/35" : "text-arl-sun"
                                    }`}
                                >
                                    <span className="sm:hidden">{d.day}</span>
                                    <span className="hidden sm:inline">{d.short}</span>
                                </p>
                                <p
                                    className={`font-display leading-tight ${
                                        d.closed
                                            ? "text-arl-ink/40 italic text-sm"
                                            : "text-[15px]"
                                    }`}
                                >
                                    {d.closed ? "Fermé" : (
                                        <>
                                            {d.hours.split(" – ")[0]}
                                            <br />
                                            <span className="text-arl-cream/45">↓</span>
                                            <br />
                                            {d.hours.split(" – ")[1]}
                                        </>
                                    )}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

export default function NousRetrouver() {
    return (
        <div data-testid="contact-page">
            {/* HERO */}
            <section className="relative overflow-hidden pt-12 pb-10 sm:pt-16 sm:pb-12">
                <div className="absolute inset-0 rhombus-bg opacity-60 pointer-events-none" />
                <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
                    <h1
                        className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-arl-ink"
                        data-testid="contact-title"
                    >
                        Nous retrouver,
                        <br />
                        <span className="font-script text-arl-blue text-6xl sm:text-7xl lg:text-[5rem] inline-block leading-none">
                            au 18 rue Gambetta.
                        </span>
                    </h1>
                    <p className="mt-6 text-lg text-arl-ink/75 max-w-2xl mx-auto leading-relaxed">
                        En plein cœur de Bourg-en-Bresse. Passez nous voir pour une boule, un goûter,
                        un café ou pour parler d'une commande sur mesure.
                    </p>
                </div>
            </section>

            {/* CONTACT + MAP */}
            <section className="relative pb-6 sm:pb-8" data-testid="contact-main">
                <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
                    <div className="lg:col-span-5 flex flex-col gap-3" data-testid="contact-cards">
                        <ContactCard
                            icon={MapPin}
                            label="Adresse"
                            value={
                                <>
                                    18 Rue Gambetta
                                    <br />
                                    01000 Bourg-en-Bresse
                                </>
                            }
                            href={MAP_LINK}
                            external
                            testid="contact-card-address"
                        />
                        <ContactCard
                            icon={Phone}
                            label="Téléphone"
                            value={PHONE}
                            href={`tel:${PHONE_RAW}`}
                            testid="contact-card-phone"
                        />
                        <ContactCard
                            icon={Mail}
                            label="E-mail"
                            value={EMAIL}
                            href={`mailto:${EMAIL}`}
                            testid="contact-card-email"
                        />
                        <ContactCard
                            icon={Instagram}
                            label="Instagram"
                            value={INSTAGRAM_HANDLE}
                            href={INSTAGRAM_URL}
                            external
                            testid="contact-card-instagram"
                        />
                    </div>

                    <div className="lg:col-span-7 flex flex-col">
                        <div className="relative rounded-[1.8rem] overflow-hidden shadow-xl border border-arl-blue/10 bg-white flex-1 min-h-[420px]">
                            <iframe
                                title="Plan de la boutique Arlequin à Bourg-en-Bresse"
                                src={MAP_EMBED}
                                className="block w-full h-full min-h-[420px]"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="contact-map-embed"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* HORAIRES */}
            <ScheduleStrip />

            {/* COMMANDES & ÉVÉNEMENTS SUR MESURE */}
            <section className="relative pb-24" data-testid="contact-custom">
                <div className="mx-auto max-w-6xl px-5 sm:px-8">
                    <div className="relative overflow-hidden rounded-[2rem] bg-arl-blue text-arl-cream p-8 sm:p-12">
                        <div className="absolute inset-0 opacity-15 rhombus-bg pointer-events-none" />

                        <div className="relative grid md:grid-cols-12 gap-8 items-center">
                            <div className="md:col-span-7">
                                <p className="text-xs uppercase tracking-[0.24em] text-arl-sun font-semibold inline-flex items-center gap-2">
                                    <Sparkles size={14} /> Sur mesure
                                </p>
                                <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
                                    Une commande,
                                    <span className="font-script text-arl-sun"> un événement&nbsp;?</span>
                                </h2>
                                <p className="mt-5 text-arl-cream/85 leading-relaxed max-w-xl">
                                    Mariages, anniversaires, repas pro, soirées entre amis ou simple
                                    surprise à la maison : Thomas imagine pour vous des créations sur
                                    mesure, parfums, formats et dates au choix. Contactez-nous pour en
                                    parler, on construit la pièce avec vous.
                                </p>
                            </div>

                            <div className="md:col-span-5 flex flex-col gap-3">
                                <a
                                    href={`mailto:${EMAIL}?subject=Demande%20de%20commande%20sur%20mesure`}
                                    className="inline-flex items-center justify-between gap-3 rounded-2xl bg-arl-cream text-arl-blue p-5 hover:bg-arl-sun hover:text-arl-ink transition-colors shadow-lg"
                                    data-testid="contact-custom-email"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="w-10 h-10 rounded-xl bg-arl-blue/10 text-arl-blue flex items-center justify-center">
                                            <Mail size={18} />
                                        </span>
                                        <div>
                                            <p className="text-[11px] uppercase tracking-wider opacity-70 font-semibold">
                                                Par e-mail
                                            </p>
                                            <p className="font-display text-base leading-tight">
                                                {EMAIL}
                                            </p>
                                        </div>
                                    </div>
                                    <ArrowRight size={18} />
                                </a>
                                <a
                                    href={`tel:${PHONE_RAW}`}
                                    className="inline-flex items-center justify-between gap-3 rounded-2xl bg-arl-cream/10 backdrop-blur border border-arl-cream/20 text-arl-cream p-5 hover:bg-arl-cream/15 transition-colors"
                                    data-testid="contact-custom-phone"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="w-10 h-10 rounded-xl bg-arl-cream/15 flex items-center justify-center">
                                            <Phone size={18} />
                                        </span>
                                        <div>
                                            <p className="text-[11px] uppercase tracking-wider text-arl-cream/70 font-semibold">
                                                Par téléphone
                                            </p>
                                            <p className="font-display text-base leading-tight">
                                                {PHONE}
                                            </p>
                                        </div>
                                    </div>
                                    <ArrowRight size={18} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
